import React, { useState } from 'react';
import { submitVisit, completeVisitUpload } from '../utils/api';
import { uploadFilesToS3 } from '../utils/uploadToS3';
import './RecordVisitModal.css';

const RecordVisitModal = ({ stone, isOpen, onClose, onSuccess }) => {
  const [visitType, setVisitType] = useState('in-person');
  const [memoryText, setMemoryText] = useState('');
  const [privacyLevel, setPrivacyLevel] = useState('everyone');
  const [mediaFiles, setMediaFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [uploadsInProgress, setUploadsInProgress] = useState(false);
  const [error, setError] = useState('');

  // Clean up object URLs when modal closes or component unmounts
  React.useEffect(() => {
    return () => {
      mediaFiles.forEach(file => {
        if (file.previewUrl) {
          URL.revokeObjectURL(file.previewUrl);
        }
      });
    };
  }, [mediaFiles]);

  // Warn before closing if uploads in progress
  React.useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (uploadsInProgress) {
        e.preventDefault();
        e.returnValue = 'Media files are still uploading. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [uploadsInProgress]);

  // Auto-close after success
  React.useEffect(() => {
    if (submitSuccess && !uploadsInProgress) {
      const timer = setTimeout(() => {
        onClose();
        // Reset form after closing
        setTimeout(() => {
          setSubmitSuccess(false);
          setVisitType('in-person');
          setMemoryText('');
          setPrivacyLevel('everyone');
          setMediaFiles([]);
        }, 300);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [submitSuccess, uploadsInProgress, onClose]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate file types first
    const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/heic', 'image/webp'];
    const allowedVideoTypes = ['video/mp4', 'video/quicktime', 'video/mov', 'video/avi', 'video/x-m4v'];
    const allowedTypes = [...allowedImageTypes, ...allowedVideoTypes];
    
    const invalidFiles = files.filter(file => !allowedTypes.includes(file.type.toLowerCase()));
    
    if (invalidFiles.length > 0) {
      setError(`Invalid file type: ${invalidFiles[0].name}. Please use JPG, PNG, GIF, HEIC, WEBP for photos or MP4, MOV for videos.`);
      e.target.value = '';
      return;
    }
    
    // Validate file count
    const currentPhotoCount = mediaFiles.filter(f => f.type === 'image').length;
    const currentVideoCount = mediaFiles.filter(f => f.type === 'video').length;
    const newPhotos = files.filter(f => f.type.startsWith('image/'));
    const newVideos = files.filter(f => f.type.startsWith('video/'));
    
    if (currentPhotoCount + newPhotos.length > 20) {
      setError('Maximum 20 photos per visit');
      e.target.value = '';
      return;
    }
    
    if (currentVideoCount + newVideos.length > 5) {
      setError('Maximum 5 videos per visit');
      e.target.value = '';
      return;
    }
    
    // Validate file sizes
    for (const file of files) {
      if (file.size > 150 * 1024 * 1024) { // 150MB
        setError(`${file.name} is too large (max 150MB)`);
        e.target.value = '';
        return;
      }
    }
    
    setError('');

    // Create local previews only
    const newItems = files.map(file => ({
      type: file.type.startsWith('image/') ? 'image' : 'video',
      file,
      fileName: file.name,
      previewUrl: URL.createObjectURL(file),
      s3Key: null
    }));
    
    setMediaFiles(prev => [...prev, ...newItems]);
  };

  const removeFile = (index) => {
    const toRemove = mediaFiles[index];
    if (toRemove?.previewUrl) {
      URL.revokeObjectURL(toRemove.previewUrl);
    }
    setMediaFiles(mediaFiles.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      // Phase 1: Submit visit and get presigned URLs
      const mediaFilesMetadata = mediaFiles.map(item => ({
        fileName: item.file.name,
        fileType: item.file.type,
        fileSize: item.file.size
      }));

      const response = await submitVisit({
        stoneId: stone.id,
        visitType,
        memoryText: memoryText.trim(),
        privacyLevel,
        mediaFilesMetadata: mediaFilesMetadata.length > 0 ? mediaFilesMetadata : undefined
      });

      const { visitId, presignedUrls } = response;

      // Show success immediately!
      setSubmitSuccess(true);
      setSubmitting(false);
      
      // Clean up preview URLs
      mediaFiles.forEach(file => {
        if (file.previewUrl) {
          URL.revokeObjectURL(file.previewUrl);
        }
      });
      
      // Call onSuccess if provided
      try {
        if (onSuccess) onSuccess();
      } catch (err) {
        console.warn('onSuccess callback error:', err);
      }

      // Phase 2: Upload files in background (silently)
      if (presignedUrls && presignedUrls.length > 0) {
        uploadInBackground(visitId, mediaFiles.map(m => m.file), presignedUrls);
      }

    } catch (err) {
      console.error('Submit error:', err);
      setError(err.message || 'Failed to submit visit. Please try again.');
      setSubmitting(false);
    }
  };

  // Background upload function (fire and forget)
  const uploadInBackground = async (visitId, files, presignedUrls) => {
    try {
      setUploadsInProgress(true);
      console.log('Starting background upload...');
      
      // Upload files to S3
      const uploadedFiles = await uploadFilesToS3(files, presignedUrls);
      
      console.log('Files uploaded, calling completion API...');
      
      // Call completion Lambda
      await completeVisitUpload({
        visitId,
        mediaFiles: uploadedFiles
      });
      
      console.log('Visit upload completed successfully');
      
    } catch (error) {
      console.error('Background upload failed:', error);
      // Don't show error to user - they already got success message
      // Admin will see the visit without media and can follow up
    } finally {
      setUploadsInProgress(false);
    }
  };

  if (!isOpen) return null;

  // Show success state
  if (submitSuccess) {
    return (
      <div className="visit-modal-overlay">
        <div className="visit-modal-content submission-status">
          <div className="success-icon">✓</div>
          <h2>Visit Submitted!</h2>
          <p>Thank you for sharing your visit. Jeff will review it soon.</p>
          {uploadsInProgress && (
            <p className="upload-notice">📤 Media uploading in background...</p>
          )}
          {!uploadsInProgress && (
            <p className="auto-close-notice">Closing in a moment...</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="visit-modal-overlay" onClick={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}>
      <div className="visit-modal-content">
        <button className="modal-close" onClick={onClose}>×</button>
        
        <h2>Record a Visit</h2>
        <p className="visit-modal-subtitle">
          Share your experience visiting <strong>{stone?.name}</strong>
        </p>
        
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <label className="section-label">Visit Type</label>
            <div className="visit-type-buttons">
              <button
                type="button"
                className={`visit-type-button ${visitType === 'in-person' ? 'active' : ''}`}
                onClick={() => setVisitType('in-person')}
              >
                🏃 In-Person Visit
                <span className="button-hint">Physically visited the stone</span>
              </button>
              <button
                type="button"
                className={`visit-type-button ${visitType === 'remote' ? 'active' : ''}`}
                onClick={() => setVisitType('remote')}
              >
                💭 Visit from Afar
                <span className="button-hint">Remembering from a distance</span>
              </button>
            </div>
          </div>
          
          <div className="form-section">
            <label className="section-label">Share a Memory or Thought (Optional)</label>
            <textarea
              value={memoryText}
              onChange={(e) => setMemoryText(e.target.value)}
              placeholder="What does this place mean to you? What are you thinking about?"
              rows="5"
              disabled={submitting}
            />
          </div>
          
          <div className="form-section">
            <label className="section-label">Photos & Videos (Optional)</label>
            <p className="field-hint">Up to 20 photos and 5 videos, max 150MB each</p>
            
            <input
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={handleFileSelect}
              disabled={submitting}
              style={{ display: 'none' }}
              id="file-input"
            />
            
            <label htmlFor="file-input" className="file-upload-button">
              Choose Files
            </label>

            {error && (
              <div className="upload-error-message">
                {error}
              </div>
            )}
            
            {mediaFiles.length > 0 && (
              <div className="media-preview">
                {mediaFiles.map((file, index) => (
                  <div key={index} className="media-preview-item">
                    {file.type === 'image' && (
                      <img src={file.previewUrl} alt={file.fileName} />
                    )}
                    {file.type === 'video' && (
                      <div className="video-preview">
                        <video src={file.previewUrl} />
                        <div className="video-icon">▶</div>
                      </div>
                    )}
                    <button
                      type="button"
                      className="remove-file"
                      onClick={() => removeFile(index)}
                      disabled={submitting}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="form-section">
            <label className="section-label">Who can see this?</label>
            <select
              value={privacyLevel}
              onChange={(e) => setPrivacyLevel(e.target.value)}
              disabled={submitting}
            >
              <option value="everyone">Everyone (friends & family)</option>
              <option value="family">Jeffrey's Family Only</option>
            </select>
          </div>
          
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
          
          <div className="moderation-notice">
            <strong>📋 Quick note:</strong> The family reviews all visits before they're published. 
            You'll get an email when your visit is approved - usually within a day or two!
          </div>
          
          <div className="form-actions">
            <button
              type="button"
              onClick={onClose}
              className="cancel-button"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit Visit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecordVisitModal;